import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  ReceiveDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type ReceiveDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderDateControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'
import { useDeviceProfileNetwork, type CredentialGroup } from '@/hooks/useDeviceProfileNetwork'

const log = AppLogger.getLogger(import.meta)

// Which network group each credential field belongs to. 'common' fields always show.
const FIELD_NETWORK_GROUP: Record<string, CredentialGroup | 'common'> = {
  deviceDeliveryDate: 'common',
  deviceSerialNumber: 'common',
  deviceEUI: 'lora',
  deviceJoinEUI: 'lora',
  deviceAppKey: 'lora',
  deviceAddress: 'lora',
  netsKey: 'lora',
  appsKey: 'lora',
  simCardNumber: 'cellular',
  ssid: 'wifi',
  wifiPassword: 'wifi',
}

// The required credential fields for each network group (enforced in the composer,
// not the schema — see Feature 4).
const REQUIRED_BY_GROUP: Record<CredentialGroup, string[]> = {
  lora: ['deviceEUI', 'deviceJoinEUI', 'deviceAppKey'],
  cellular: ['simCardNumber'],
  wifi: ['ssid', 'wifiPassword'],
}

// Re-export schema and types
export { ReceiveDeviceFormSchema, type ReceiveDeviceFormData } from './schema'

export type ReceiveDeviceComposerProps = {
  deviceNumber: number
  deviceProcurementInfoNumber?: number
  deviceDeliveryDate?: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof ReceiveDeviceFormData, value: unknown): string | null => {
  const shape = ReceiveDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// The backend (upsertDeviceKeyInfo) enforces strict hex formats that the shared
// Zod schema (length-only) does not. We mirror those backend rules here as extra
// onBlur + onSubmit validation so the user is warned before a request is sent —
// the schema stays the source of truth and is left untouched.
const validateKeyFormat = (name: keyof ReceiveDeviceFormData, value: unknown): string | null => {
  // Emptiness / length is handled by the schema check; only validate format here.
  if (typeof value !== 'string' || value.trim() === '') return null

  if (name === 'deviceEUI' || name === 'deviceJoinEUI') {
    // Backend pattern is 8 hex octets (e.g. AA:BB:CC:DD:EE:FF:00:11). Separators
    // are auto-inserted on submit, so accept 16 hex chars with or without : / -.
    const cleaned = value.replace(/[:-]/g, '')
    if (!/^[0-9A-Fa-f]{16}$/.test(cleaned)) {
      return 'Must be 16 hexadecimal characters (e.g. AABBCCDDEEFF0011)'
    }
  }

  if (name === 'deviceAppKey') {
    if (!/^[a-fA-F0-9]{32}$/.test(value.trim())) {
      return 'APP Key must be exactly 32 hexadecimal characters'
    }
  }

  return null
}

export function ReceiveDeviceComposer(props: ReceiveDeviceComposerProps) {
  const { deviceNumber, deviceProcurementInfoNumber, deviceDeliveryDate, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  // Fetch existing procurementInfo for merging (must be at top level)
  const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber });
  const deviceData = deviceDataRaw?.content;

  // The credential fields shown/required depend on the device's network type,
  // resolved from its Device Profile (defaults to the LoRa set until the backend
  // device↔profile link exists — spec Feature 4/6).
  const { credentialGroup } = useDeviceProfileNetwork(deviceData);

  const receiveProcurement = useApiMutation('upsertDeviceProcurementInfo', 'POST', {
    onError: (error) => {
      onError?.(error)
    },
  })
  const receiveDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => {
      onError?.(error)
    },
  })
  const upsertKeyInfo = useApiMutation('upsertDeviceKeyInfo', 'POST', {
    onError: (error) => {
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: ReceiveDeviceFormSchema,
    defaultValues: {
      deviceDeliveryDate: deviceDeliveryDate
        ? new Date(deviceDeliveryDate * 1000).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      deviceSerialNumber: '',
      deviceEUI: '',
      deviceJoinEUI: '',
      deviceAppKey: '',
      deviceAddress: '',
      netsKey: '',
      appsKey: '',
    } as ReceiveDeviceFormData,
    onSubmit: async ({ value }) => {
      const formValue = value as ReceiveDeviceFormData
      // Wait for device data to load
      if (isDeviceLoading) return;

      // Enforce the required credential fields for the device's network group
      // (schema keeps them optional; requiredness is per-group here).
      const requiredFields = REQUIRED_BY_GROUP[credentialGroup] ?? []
      let hasMissing = false
      requiredFields.forEach((field) => {
        const val = (formValue as any)[field]
        if (typeof val !== 'string' || val.trim() === '') {
          hasMissing = true
          form.setFieldMeta(field as any, (meta) => ({ ...meta, isTouched: true, errors: ['This field is required'] }))
        }
      })
      if (hasMissing) return;

      // LoRa only: enforce the backend's strict hex formats before any request is
      // sent, so an invalid payload never reaches upsertDeviceKeyInfo.
      if (credentialGroup === 'lora') {
        const keyFields: Array<keyof ReceiveDeviceFormData> = ['deviceEUI', 'deviceJoinEUI', 'deviceAppKey']
        let hasFormatError = false
        keyFields.forEach((field) => {
          const message = validateKeyFormat(field, formValue[field])
          if (message) {
            hasFormatError = true
            form.setFieldMeta(field as any, (meta) => ({ ...meta, isTouched: true, errors: [message] }))
          }
        })
        if (hasFormatError) return;
      }

      try {
        // Get existing deviceKeyInfoNumber if it exists
        const existingDeviceKeyInfoNumber = (deviceData?.device as any)?.deviceKeyInfoNumber;

        // Build payloads
        const { procurementInfo: newProcurementInfo, deviceKeyInfo } =
          transformToApiRequest(
            formValue,
            deviceNumber,
            deviceProcurementInfoNumber,
            existingDeviceKeyInfoNumber
          );

        // STEP 1: Upsert Device Key Info (MUST be first to get deviceKeyInfoNumber)
        // Note: Backend expects fields at root level, not wrapped in deviceKeyInfo
        const keyInfoResponse = await upsertKeyInfo.mutateAsync(deviceKeyInfo);
        const createdKeyInfoNumber = keyInfoResponse?.content?.deviceKeyInfo?.deviceKeyInfoNumber;

        if (!createdKeyInfoNumber) {
          throw new Error('Failed to create device key info: No deviceKeyInfoNumber returned');
        }

        // STEP 2: Update Device with deviceKeyInfoNumber link
        const existingProcurementInfo = deviceData?.procurementInfo || {};
        const mergedDevice = {
          ...deviceData?.device,
          deviceNumber,
          deviceStatus: 'received',
          deviceKeyInfoNumber: createdKeyInfoNumber,
        };
        await receiveDevice.mutateAsync({ device: mergedDevice });

        // STEP 3: Update Procurement Info
        const mergedProcurementInfo = {
          ...existingProcurementInfo,
          ...newProcurementInfo,
          deviceProcurementStatus: 'received',
        };
        await receiveProcurement.mutateAsync({ deviceNumber, procurementInfo: mergedProcurementInfo });

        // All steps completed successfully - trigger refresh
        onSuccess?.();
      } catch (error) {
        log.error('Failed to mark device as received:', error);
        throw error;
      }
    },
  })

  const renderControl = (control: any) => {
    // Show only credential fields belonging to the device's network group;
    // 'common' fields (date, serial) always show.
    const fieldGroup = FIELD_NETWORK_GROUP[control.name] ?? 'common'
    if (fieldGroup !== 'common' && fieldGroup !== credentialGroup) return null

    const isRequired =
      fieldGroup === 'common'
        ? control.required
        : (REQUIRED_BY_GROUP[credentialGroup]?.includes(control.name) ?? false)

    if (control.htmlControl === 'date') {
      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            onBlur: ({ value }) => validateField(control.name as keyof ReceiveDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: control.disabled ?? false,
              type: 'date'
            }
            return renderDateControl(config, bound)
          }}
        </form.AppField>
      )
    }

    // Default to text input
    return (
      <form.AppField 
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof ReceiveDeviceFormData, value)
            || validateKeyFormat(control.name as keyof ReceiveDeviceFormData, value)
            || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: 'text'
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        {groups.map((group) => {
          const groupControls = controlsByGroup.get(group.formGroupName) || []
          if (groupControls.length === 0) return null
          return (
            <div key={group.formGroupName} className="space-y-4">
              {groupControls.map(renderControl)}
            </div>
          )
        })}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}
          
          {renderButtonControl({
            label: (receiveProcurement.isPending || receiveDevice.isPending || upsertKeyInfo.isPending)
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Submitting...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Receive Device'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}
